import { useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { NavHistoryContext } from '../Contexts/NavHistoryContext';

export function useClientNavigation(client) {
  const navigate = useNavigate();
  const location = useLocation();
  const navHistory = useContext(NavHistoryContext);

  const cleanState = (st) => {
    if (!st) return null;
    const { previousLocation, ...rest } = st;
    return rest;
  };

  const capturePreviousLocation = () => ({
    pathname: location.pathname,
    search: location.search || '',
    hash: location.hash || '',
    state: cleanState(location.state),
  });

  const withState = (path, type, breadcrumbs = [], extraState = {}) =>
    navigate(path, {
      state: {
        type,
        client,
        ...extraState,
        breadcrumbs,
        previousLocation: capturePreviousLocation(),
      },
    });

  const navigateWithPrevious = (path, state) =>
    navigate(path, {
      state: {
        ...state,
        previousLocation: capturePreviousLocation(),
      },
    });

  return {
    viewClientDetails: () =>
      navigate('/client-details', {
        replace: true,
        state: {
          client,
          breadcrumbs: ['ClientPage'],
          previousLocation: capturePreviousLocation(),
        },
      }),
    viewDocumentDetails: (type) =>
      withState('/Document', type, ['AccountingManagement', type]),

    // sortCodeId identifies the sort code unambiguously; sortCodeName is only a fallback
    // and cannot be resolved reliably when several sort codes share a name.
    viewSortCodeReport: (sortCodeName = '', dateState = {}, sortCodeId = null) =>
      navigateWithPrevious('/SortCodeReport', {
        type: 'ProfitAndLossReport',
        client,
        groupName: '',
        sortCodeName,
        sortCodeId,
        breadcrumbs: ['Reports', 'ProfitAndLossReport'],
        ...dateState,
      }),

    viewJournalEntryDetails: (type) =>
      withState('/JournalEntry', type, ['AccountingManagement', type]),

    viewOtherDocumentDetails: (type, subItem = null, isPermenantCase = false, title = null) =>
      navigateWithPrevious('/OtherDocument', {
        type,
        client,
        subItem,
        isPermenantCase: title ? false : isPermenantCase,
        titleId: title?.id || null,
        titleName: title?.name || null,
        breadcrumbs: ['Archive', 'OtherDocuments'],
        from: 'OtherDocument',
      }),

    viewClientItems: (type) =>
      withState('/ClientItems', type, ['Sales', type]),

    viewPaymentAcceptanceDetails: (type, extraState = {}) =>
      withState('/PaymentAcceptance', type, ['AccountingManagement', type], extraState),

    viewProfitAndLossDetails: (type) =>
      withState('/ProfitAndLossReport', type, ['Reports', type]),

    viewProfittAndLoss6111: (type) =>
      withState('/ProfittAndLoss6111', type, ['Reports', type]),

    viewReport1214: (type) =>
      withState('/Report1214', type, ['Reports', type]),

    viewReport1214B: (type) =>
      withState('/Report1214', type , ['Reports', type ], {
        initialTab: 'annex1214b',
      }),

    viewReport1281: (type) =>
      withState('/Report1214', type , ['Reports', type], {
        initialTab: 'annex1281',
      }),

    viewReport1362: (type) =>
      withState('/Report1214', type , ['Reports', type], {
        initialTab: 'annex1362',
      }),

    viewTransactionDocuments: (type, showCancelModal = false, extraState = {}) => {
      const { breadcrumbs = ['Sales', type], ...restExtra } = extraState;
      return withState('/TransactionDocuments', type, breadcrumbs, {
        showCancelModal,
        ...restExtra,
      });
    },

    viewLedgerReport: (type, isProfitAndLoss = false) =>
      navigateWithPrevious('/LedgerReport', {
        type,
        client,
        isProfitAndLoss,
        breadcrumbs: ['Reports', type],
      }),

    viewAccountCardDashboard: (
      selectionMode = 'single',
      selectedCards = [],
      fromCard = null,
      toCard = null,
      selectAllCards = false
    ) =>
      navigateWithPrevious('/AccountCardDashboard', {
        type: 'AccountCardDashboard',
        client,
        cardSelectionMode: selectionMode,
        selectedCards,
        fromCard,
        toCard,
        selectAllCards,
        breadcrumbs: ['Reports', 'DebtorsCreditorsLedgerReport'],
      }),

    viewAccountCardsTable: (type, breadcrumbs = ['Settings', type]) =>
      withState('/AccountCardsTable', type, breadcrumbs),

    viewReceivables: (type = 'Receivables') =>
      withState('/Receivables', type, ['Sales', type]),

    viewReceivablesAging: (type = 'ReceivablesAging') =>
      withState('/ReceivablesAging', type, ['Reports', 'ReceivablesAging']),

    /** extraState may carry cardIds / startDate / endDate / currency from the caller. */
    viewCashFlowForecast: (type = 'CashFlowForecast', extraState = {}) =>
      withState('/CashFlowForecast', type, ['Reports', 'CashFlowForecast'], extraState),

    viewForeignTravelReport: (type = 'ForeignTravelReport') =>
      withState('/ForeignTravelReport', type, ['Reports', 'ForeignTravelReport']),

    viewClientExpenseAssignmentReport: (type = 'ClientExpenseAssignmentReport') =>
      withState('/ClientExpenseAssignmentReport', type, ['Reports', 'ClientExpenseAssignmentReport']),

    viewBankTransactions: (type) =>
      withState('/BankTransactions', type, ['Matching', type]),

    viewCollectionPage: (type) =>
      withState('/CollectionPage', type, ['CRM', type]),

    viewAssignments: (type) =>
      withState('/Assignments', type, ['CRM', type]),

    viewUserActivity: (type) =>
      withState('/user-activities', type, ['CRM', type]),

    viewCardMatching: (type) =>
      withState('/CardMatching', type, ['Matching', type]),

    viewBankPage: (type) =>
      withState('/BankPages', type, ['Matching', type]),

    viewAssets: (type) =>
      withState('/Assets', type, ['AccountingManagement', type]),

    viewProjects: (type) =>
      withState('/Projects', type, ['Settings', type]),

    viewHoldingItems: (type) =>
      withState('/HoldingItems', type, ['AccountingManagement', type]),

    viewClarificationDocuments: (type) =>
      withState('/DocumentsRequiringClarification', type, ['AccountingManagement', type]),

    viewInventoryItems: (type) =>
      withState('/InventoryManagement', type, ['Sales', type]),

    viewVehicleManagement: (type) =>
      withState('/VehicleManagement', type, [type]),

    viewGarageWorkDiary: (type) =>
      withState('/GarageWorkDiary', type, [type]),

    viewAudit: (type = 'Audit') =>
      withState('/Audit', type, ['Audit']),

    viewAuditReports: (type) =>
      withState('/AuditReports', type, [type]),

    viewAssignmentsByClient: (type) =>
      withState('/Assignments', type, ['Assignments', type]),

    viewDocumentCreate: (screen, type, duplicateData = null) =>
      navigateWithPrevious(screen, {
        client,
        type,
        ...(duplicateData ? { item: duplicateData } : {}),
        breadcrumbs: ['Sales', type],
      }),

    viewAddClient: (isEdit = false) =>
      navigateWithPrevious('/AddClient', {
        client,
        isEdit,
        breadcrumbs: [isEdit ? 'EditClient' : 'addClientTitle'],
      }),

    viewAccountCardDashboardBAccountCard: (
      accountCardId,
      accountCardName,
      palCodeName,
      startDate,
      endDate
    ) =>
      navigateWithPrevious('/AccountCardDashboard', {
        client,
        accountCardId,
        accountCardName,
        palCodeName,
        startDate,
        endDate,
        isSortCodeReport: true,
        breadcrumbs: ['Reports', 'LedgerReport'],
      }),

    navigateToPrevious: (currentLocation = location) => {
      if (navHistory?.navigateBack) {
        navHistory.navigateBack({ currentLocation, client });
        return;
      }

      const path = (currentLocation.pathname || '').toLowerCase();
      if (path === '/client-details') {
        navigate('/Main', { replace: true });
        return;
      }

      const prev = currentLocation.state?.previousLocation;
      if (prev?.pathname) {
        navigate(
          {
            pathname: prev.pathname,
            search: prev.search || '',
            hash: prev.hash || '',
          },
          { state: prev.state ?? null, replace: true }
        );
        return;
      }

      const clientForBack = client ?? currentLocation.state?.client;
      if (clientForBack) {
        navigate('/client-details', {
          replace: true,
          state: { client: clientForBack, breadcrumbs: ['ClientPage'] },
        });
        return;
      }

      if (window.history.length > 1) {
        navigate(-1);
        return;
      }

      navigate('/Main', { replace: true });
    },

    viewSubAccountants: () =>
      withState('/SubAccountants', null, ['OfficeAccountManagers']),

    viewTrackingList: (type) =>
      withState('/TrackingList', type, ['CRM', type]),

    viewAdvanceCalculator: (type) =>
      withState('/AdvanceCalculator', type, ['CRM', type]),

    viewSignatureDocument: (type) =>
      withState('/SignatureDocument', type, ['Archive', 'SignatureDocuments']),

    viewForms: (type) =>
      withState('/Forms', type, ['Archive', 'Forms']),

    viewAnnualReportQuestionnaire: (type) =>
      withState('/AnnualReportQuestionnaire', type, ['Reports', type]),

    viewGrantsStatus: (type = 'GrantsStatus') =>
      withState('/GrantsStatus', type, ['Reports', type]),

    viewAccountantDetails: () =>
      navigateWithPrevious('/Accountant-details', {
        breadcrumbs: ['Accountant-details'],
      }),
  };
}
